import type {
    AdoptionRequirement,
    RequirementKind,
    RequirementSeverity,
    RequirementSource,
} from "./types.ts";

export type CompatibilityListingInput = {
    name?: string | null;
    description?: string | null;
    organizationName?: string | null;
    organizationDescription?: string | null;
    organizationState?: string | null;
    isGoodWithKids?: boolean | null;
    isGoodWithDogs?: boolean | null;
    isGoodWithCats?: boolean | null;
};

type Pattern = {
    kind: RequirementKind;
    severity: RequirementSeverity;
    label: string;
    confidence: number;

    any: RegExp[];

    not?: RegExp[];
};

const PATTERNS: Pattern[] = [
    {
        kind: "house_required",
        severity: "hard",
        label: "House required (no apartment)",
        confidence: 0.85,
        any: [
            /\bno (?:apartments?|condos?|apartment dwellers)\b/,
            /\bnot? (?:suitable|recommended|appropriate|ideal) for (?:an? )?apartments?\b/,
            /\bapartments? (?:are )?not (?:permitted|allowed|suitable|acceptable)\b/,
            /\bsingle[- ]family (?:house|home)\b/,
            /\bmust (?:have|live in|be in|reside in) a house\b/,
            /\bhouse (?:is )?required\b/,
        ],
        not: [
            /\b(?:great|good|perfect|fine|suitable|happy|content|ok(?:ay)?) (?:for|in|living in) (?:an? )?apartments?\b/,
            /\bapartment[- ]?friendly\b/,
            /\bapartments? (?:are )?(?:ok(?:ay)?|fine|welcome)\b/,
        ],
    },
    {
        kind: "fenced_yard",
        severity: "hard",
        label: "Fenced yard required",
        confidence: 0.85,
        any: [
            /\b(?:securely |fully |physically |6[- ]?(?:ft|foot) )?fenced(?:[- ]in)?[- ]?yard (?:is )?(?:required|a must|necessary|needed|mandatory)\b/,
            /\b(?:require[sd]?|must have|need(?:s|ed)? a|needs? to have a)[^.!?]{0,18}\bfenced(?:[- ]in)?\b/,
            /\bfencing (?:is )?(?:required|a must|mandatory|necessary)\b/,
            /\bno (?:invisible|electric|underground|wireless) fence(?:s|ing)?\b/,
        ],
        not: [
            /\bno (?:fenced |physical )?(?:yard|fence|fencing) (?:is )?(?:required|needed|necessary)\b/,
            /\bfenced yard (?:is )?(?:not required|a (?:plus|bonus|preference)|preferred)\b/,
            /\b(?:if|whether) (?:the |your )?yard (?:is|was)? ?fenced\b/,
        ],
    },
    {
        kind: "yard_required",
        severity: "hard",
        label: "Yard required",
        confidence: 0.78,
        any: [
            /\b(?:require[sd]?|must have|need(?:s|ed)? a|needs? to have a)[^.!?]{0,18}\byard\b/,
            /\byard (?:is )?(?:required|a must|necessary|mandatory)\b/,
            /\bmust have (?:access to )?a (?:large )?(?:yard|outdoor space)\b/,
        ],
        not: [
            /\bno yard (?:is )?(?:required|needed|necessary)\b/,
            /\byard (?:is )?(?:not required|a (?:plus|bonus|preference)|preferred|not necessary)\b/,
            /\b(?:does(?:n't| not) need|doesn't require) a yard\b/,
        ],
    },
    {
        kind: "home_ownership",
        severity: "hard",
        label: "Homeowners only",
        confidence: 0.82,
        any: [
            /\bno renters?\b/,
            /\brenters? (?:are )?(?:not|ineligible)\b/,
            /\bmust own (?:your|their)? (?:own )?(?:home|house)\b/,
            /\bhome ?owners? only\b/,
            /\bmust be a home ?owner\b/,
            /\bno rentals?\b/,
        ],
    },
    {
        kind: "landlord_approval",
        severity: "soft",
        label: "Landlord approval (if renting)",
        confidence: 0.7,
        any: [
            /\blandlord (?:approval|permission|letter|verification|contact|consent)\b/,
            /\bif (?:you )?rent[^.!?]{0,48}\blandlord\b/,
            /\bproof (?:of )?(?:home ?ownership|pet[- ]?policy)\b/,
        ],
    },
    {
        kind: "in_state_only",
        severity: "hard",
        label: "In-state adopters only",
        confidence: 0.8,
        any: [
            /\bno out[- ]of[- ]state\b/,
            /\bout[- ]of[- ]state (?:adopters?|applications?|adoptions?|homes?) (?:are )?not\b/,
            /\blocal (?:adopters?|homes?|adoptions?|applicants?) only\b/,
            /\bmust (?:live|reside|be located|be based) (?:in|within)\b/,
            /\bin[- ]state (?:adopters?|homes?|applicants?) only\b/,
            /\bwithin \d{2,3} ?(?:miles|mi)\b/,
            /\b(?:we )?do(?:es)? not (?:adopt|ship|transport|fly|place)[^.!?]{0,24}out of state\b/,
            /\bno (?:transport|shipping|flights|out[- ]of[- ]state transport)\b/,
        ],
        not: [
            /\bout[- ]of[- ]state (?:adopters?|adoptions?|homes?) (?:are )?(?:welcome|ok(?:ay)?|considered|fine|accepted)\b/,
        ],
    },
    {
        kind: "resident_dog_required",
        severity: "hard",
        label: "Must have another dog",
        confidence: 0.8,
        any: [
            /\bmust (?:have|go to a home with|be (?:placed|adopted) (?:with|into a home with))[^.!?]{0,32}\b(?:another|a (?:resident|current|second|companion|playmate)) dog\b/,
            /\b(?:requires?|needs?) (?:a |another )?(?:resident |canine |doggy |dog |four[- ]legged |furry )(?:companion|playmate|friend|sibling|buddy|brother|sister)\b/,
            /\bhome with (?:another|a resident) dog (?:is )?(?:required|a must|mandatory)\b/,
            /\bmust be adopted with (?:a|another) dog\b/,
        ],
    },
    {
        kind: "only_dog",
        severity: "hard",
        label: "Must be the only dog",
        confidence: 0.8,
        any: [
            /\b(?:must be|wants to be|needs to be|prefers to be|do(?:es)? best as|only as) (?:the )?only (?:dog|pet|animal)\b/,
            /\bno other (?:dogs|pets|animals)\b/,
            /\bonly (?:dog|pet) (?:in the (?:home|house|household))\b/,
            /\bsole (?:dog|pet|companion)\b/,
        ],
        not: [
            /\b(?:good|great|fine|ok(?:ay)?) with other (?:dogs|pets)\b/,
            /\bif [a-z]+ (?:is|are|was|were) (?:the )?only (?:dog|pet|animal)\b/,
        ],
    },
    {
        kind: "no_cats",
        severity: "hard",
        label: "Cat-free home required",
        confidence: 0.82,
        any: [
            /\bno cats?\b/,
            /\b(?:cannot|can't|not able to|must not) (?:live|be placed|go to a home|be adopted) with cats?\b/,
            /\bnot? cat[- ]?(?:safe|friendly|tolerant|compatible)\b/,
            /\bcat[- ]?free home\b/,
            /\bhome (?:with )?no cats?\b/,
        ],
        not: [
            /\b(?:good|great|fine|ok(?:ay)?|does well|gets along) with cats?\b/,
            /\bcat[- ]?friendly\b/,
        ],
    },
    {
        kind: "no_young_children",
        severity: "hard",
        label: "No young children",
        confidence: 0.8,
        any: [
            /\bno (?:young |small |little |toddler|infant)?(?:children|kids|toddlers)\b/,
            /\b(?:children|kids) (?:over|older than|above|must be (?:over|older than|at least)) (?:age )?\d{1,2}\b/,
            /\bno (?:children|kids) under (?:age )?\d{1,2}\b/,
            /\bnot? (?:suitable|recommended|good|ideal) (?:for|with|around|in a home with)[^.!?]{0,24}(?:young )?(?:children|kids)\b/,
            /\bhome without (?:young )?(?:children|kids)\b/,
            /\badults?[- ]only (?:home|household)\b/,
        ],
        not: [
            /\b(?:good|great|fine|ok(?:ay)?|wonderful|does well) with (?:children|kids)\b/,
            /\bkid[- ]?friendly\b/,
            /\b(?:children|kids) of all ages\b/,
        ],
    },
    {
        kind: "experienced_owner",
        severity: "soft",
        label: "Experienced owner preferred",
        confidence: 0.72,
        any: [
            /\bexperienced (?:dog |pet |breed )?(?:owner|adopter|home|handler)\b/,
            /\bnot? (?:for|recommended for|suitable for|a fit for) first[- ]?time (?:owners?|adopters?|dog owners?)\b/,
            /\b(?:prior|previous) (?:dog |breed |large[- ]dog )?experience (?:is )?(?:required|preferred|a must|needed|necessary)\b/,
            /\b(?:breed|large[- ]dog|working[- ]dog) experience\b/,
        ],
    },
];

function dedupe(requirements: AdoptionRequirement[]): AdoptionRequirement[] {
    const byKind = new Map<RequirementKind, AdoptionRequirement>();
    for (const req of requirements) {
        const existing = byKind.get(req.kind);
        if (!existing || req.confidence > existing.confidence) {
            byKind.set(req.kind, req);
        }
    }

    if (byKind.has("fenced_yard")) byKind.delete("yard_required");

    if (byKind.has("resident_dog_required") && byKind.has("only_dog")) {
        const a = byKind.get("resident_dog_required");
        const b = byKind.get("only_dog");
        if (a && b)
            byKind.delete(
                a.confidence >= b.confidence
                    ? "only_dog"
                    : "resident_dog_required",
            );
    }
    return [...byKind.values()];
}

function snippetAround(text: string, index: number, length: number): string {
    const start = Math.max(0, index - 32);
    const end = Math.min(text.length, index + length + 48);
    let s = text.slice(start, end).trim();
    if (start > 0) s = `…${s}`;
    if (end < text.length) s = `${s}…`;
    return s.replace(/\s+/g, " ");
}

function normalize(text: string): string {
    return text.replace(/\s+/g, " ").toLowerCase();
}

export function extractRequirementsFromText(
    raw: string | null | undefined,
    source: RequirementSource,
): AdoptionRequirement[] {
    if (!raw) return [];
    const text = normalize(raw);
    const out: AdoptionRequirement[] = [];

    for (const pattern of PATTERNS) {
        if (pattern.not?.some((re) => re.test(text))) continue;
        for (const re of pattern.any) {
            const m = re.exec(text);
            if (!m) continue;
            out.push({
                kind: pattern.kind,
                severity: pattern.severity,
                label: pattern.label,
                source,
                snippet: snippetAround(text, m.index, m[0].length),
                confidence: pattern.confidence,
            });
            break;
        }
    }
    return out;
}

function extractStructuredRequirements(
    listing: CompatibilityListingInput,
): AdoptionRequirement[] {
    const out: AdoptionRequirement[] = [];
    const add = (
        kind: RequirementKind,
        label: string,
        flag: boolean | null | undefined,
    ) => {
        if (flag === false) {
            out.push({
                kind,
                severity: "soft",
                label,
                source: "structured",
                confidence: 0.65,
            });
        }
    };
    add("no_cats", "May not be comfortable with cats", listing.isGoodWithCats);
    add("only_dog", "May do best as the only dog", listing.isGoodWithDogs);
    add(
        "no_young_children",
        "May not suit homes with young children",
        listing.isGoodWithKids,
    );
    return out;
}

export function extractAdoptionRequirements(
    listing: CompatibilityListingInput,
): AdoptionRequirement[] {
    return dedupe([
        ...extractRequirementsFromText(listing.description, "listing"),
        ...extractRequirementsFromText(
            listing.organizationDescription,
            "organization",
        ),
        ...extractStructuredRequirements(listing),
    ]);
}
